/**
 * Data Conversion Utilities for Schedule Gantt View
 *
 * Converts between database trip/coverage format and temporal algorithm format.
 * Enables viewing and editing existing placements in the Gantt chart.
 */

import { Shape } from "../../Settings/RampsSettings/RampEditor/RampCanvas/RampCanvas";
import { ScheduleChange } from "../AutoPark/components/SpotOccupancyGantt";
import { getCentroid } from "./geometryUtils";
import {
  aircraftToSizeCategory,
  dateToTimeBucket,
  timeBucketToDate,
} from "./temporalRampIntegration";
import { ParkingRequest, Placement } from "./types";

/**
 * Coverage segment from database (from trip_placement table)
 */
export interface CoverageSegment {
  start_time: string; // ISO timestamp
  end_time: string; // ISO timestamp
  x?: number | null;
  y?: number | null;
  angle?: number | null;
  ramp_id?: string | null;
  hangar_id?: string | null;
}

/**
 * Trip data with coverage segments
 */
export interface TripWithCoverage {
  trip_id: string;
  tenant_id: string;
  tail_number?: string;
  aircraft_type?: string;
  aircraft_model?: string;
  aircraft_manufacturer?: string;
  wingspan?: number;
  aircraft_length?: number;
  arrival_date: string;
  departure_date: string;
  fbo_id: string;
  status: "scheduled" | "active" | "completed" | "cancelled" | "requested" | "confirmed" | "tenant-based" | "transient" | "hangar";
  created_at: string;
  updated_at: string;
  version: number;
  coverage: CoverageSegment[];
}

/**
 * Result of converting database trips to temporal format
 */
export interface TemporalConversionResult {
  aircraft: ParkingRequest[];
  placement: Placement;
  tripMap: Map<string, string>; // aircraftId -> tripId mapping
}

/**
 * Check if a point is inside a polygon using ray casting algorithm
 */
function isPointInPolygon(
  point: { x: number; y: number },
  polygon: { x: number; y: number }[]
): boolean {
  if (!polygon || polygon.length < 3) {
    return false;
  }

  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;

    const intersect =
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;

    if (intersect) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * Calculate distance between two points
 */
function distance(
  p1: { x: number; y: number },
  p2: { x: number; y: number }
): number {
  return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
}

/**
 * Calculate centroid of a polygon
 */
function calculateCentroid(points: { x: number; y: number }[]): {
  x: number;
  y: number;
} {
  if (points.length === 0) {
    return { x: 0, y: 0 };
  }
  const sum = points.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), {
    x: 0,
    y: 0,
  });
  return { x: sum.x / points.length, y: sum.y / points.length };
}

/**
 * Find which spot contains the given position
 * Uses point-in-polygon test with Y-flip to match database coordinates
 *
 * @param position - Aircraft position from database (bottom-left origin)
 * @param shapes - Ramp shapes (top-left origin)
 * @param rampDepth - Ramp depth for Y-axis flip
 * @param options - Configuration options
 * @returns Spot ID or null if not found
 */
export function findSpotForPosition(
  position: { x: number; y: number },
  shapes: Shape[],
  rampDepth: number,
  options: { fallbackNearest?: boolean } = {}
): string | null {
  const spotShapes = shapes.filter((s) => s.type === "Spot");

  // First, try exact point-in-polygon test
  for (const shape of spotShapes) {
    if (!shape.points || shape.points.length === 0) {
      continue;
    }

    // Y-flip shape points to match database coordinate system (bottom-left origin)
    const flippedPoints = shape.points.map((p) => ({
      x: p.x,
      y: rampDepth - p.y,
    }));

    if (isPointInPolygon(position, flippedPoints)) {
      return shape.id;
    }
  }

  // If no exact match and fallback is enabled, find nearest spot
  if (options.fallbackNearest) {
    let nearestSpotId: string | null = null;
    let nearestDistance = Infinity;

    for (const shape of spotShapes) {
      if (!shape.points || shape.points.length === 0) {
        continue;
      }

      // Calculate centroid in database coordinates
      const flippedPoints = shape.points.map((p) => ({
        x: p.x,
        y: rampDepth - p.y,
      }));
      const centroid = calculateCentroid(flippedPoints);
      const dist = distance(position, centroid);

      if (dist < nearestDistance) {
        nearestDistance = dist;
        nearestSpotId = shape.id;
      }
    }

    if (nearestSpotId) {
      console.warn(
        `[findSpotForPosition] Position (${position.x}, ${
          position.y
        }) outside all spots, using nearest: ${nearestSpotId} (distance: ${nearestDistance.toFixed(
          1
        )})`
      );
      return nearestSpotId;
    }
  }

  return null;
}

/**
 * Convert database trips with coverage to temporal algorithm format
 *
 * @param trips - Trips with coverage segments from database
 * @param shapes - Ramp shapes for position → spot mapping
 * @param rampDepth - Ramp depth for Y-axis flip
 * @param startTime - Base time for bucket calculations
 * @param rampId - Filter to only include placements on this ramp
 * @returns Temporal format data (ParkingRequest[] + Placement + trip mapping)
 */
export function convertTripsToTemporalFormat(
  trips: TripWithCoverage[],
  shapes: Shape[],
  rampDepth: number,
  startTime: Date,
  rampId: string
): TemporalConversionResult {
  const aircraft: ParkingRequest[] = [];
  const placement: Placement = {};
  const tripMap = new Map<string, string>();

  for (const trip of trips) {
    // Use tenant_id as unique request identifier (aircraft identity)
    // This allows placement map to use aircraft_id as key for tracking where each aircraft is
    const requestId = trip.tenant_id;

    // Calculate arrival/departure buckets from trip times
    const arrivalBucket = dateToTimeBucket(trip.arrival_date, startTime);
    const departureBucket = dateToTimeBucket(trip.departure_date, startTime);

    // Build ParkingRequest
    const parkingRequest: ParkingRequest = {
      id: requestId,
      tail_number: trip.tail_number || "Unknown",
      arrivalBucket,
      departureBucket,
      sizeCategory: aircraftToSizeCategory({
        wingspan: trip.wingspan || 0,
        length: trip.aircraft_length || 0,
      } as any),
      trip_id: trip.trip_id,
      aircraft: trip.aircraft_type
        ? ({
            id: trip.tenant_id, // Aircraft identity uses tenant_id
            make: trip.aircraft_manufacturer || "",
            model: trip.aircraft_model || "",
            wingspan: trip.wingspan || 0,
            length: trip.aircraft_length || 0,
          } as any)
        : undefined,
    };

    aircraft.push(parkingRequest);
    tripMap.set(requestId, trip.trip_id);

    // Process coverage segments
    if (!trip.coverage || trip.coverage.length === 0) {
      // Unplaced aircraft - no entry in placement object
      continue;
    }

    // Filter coverage to only include segments on this ramp
    const rampCoverage = trip.coverage.filter((seg) => seg.ramp_id === rampId);

    if (rampCoverage.length === 0) {
      // No coverage on this ramp
      continue;
    }

    // Convert coverage segments to placement format
    const segments = rampCoverage
      .map((seg) => {
        // Skip segments without position data
        if (
          seg.x === null ||
          seg.x === undefined ||
          seg.y === null ||
          seg.y === undefined
        ) {
          return null;
        }

        // Find spot ID for this position
        const spotId = findSpotForPosition(
          { x: seg.x, y: seg.y },
          shapes,
          rampDepth,
          { fallbackNearest: true }
        );

        if (!spotId) {
          console.warn(
            `[convertTripsToTemporalFormat] Could not find spot for position (${seg.x}, ${seg.y}) for aircraft ${trip.tail_number}`
          );
          return null;
        }

        // Convert timestamps to buckets
        const startBucket = dateToTimeBucket(seg.start_time, startTime);
        const endBucket = dateToTimeBucket(seg.end_time, startTime);

        return {
          spotId,
          startBucket,
          endBucket,
        };
      })
      .filter((seg) => seg !== null) as Array<{
      spotId: string;
      startBucket: number;
      endBucket: number;
    }>;

    if (segments.length === 0) {
      // All segments failed to map
      continue;
    }

    // Build placement entry
    if (segments.length === 1) {
      // Single placement
      placement[requestId] = {
        spotId: segments[0].spotId,
        orientationId: "center", // Default orientation
      };
    } else {
      // Segmented placement (aircraft moved between spots)
      placement[requestId] = {
        segments: segments.map((seg) => ({
          ...seg,
          orientationId: "center", // Default orientation
        })),
        moveCount: segments.length - 1,
      };
    }
  }

  return { aircraft, placement, tripMap };
}

/**
 * Convert schedule changes back to database coverage segments
 *
 * @param changes - Schedule changes from Gantt editing
 * @param shapes - Ramp shapes for spot → position mapping
 * @param rampDepth - Ramp depth for Y-axis flip
 * @param rampId - Ramp ID for coverage segments
 * @param startTime - Base time for bucket calculations
 * @returns Map of tripId → coverage segments
 */
export function convertScheduleChangesToDB(
  changes: ScheduleChangeWithTrip[],
  shapes: Shape[],
  rampDepth: number,
  rampId: string,
  startTime: Date
): Map<string, CoverageSegment[]> {
  const result = new Map<string, CoverageSegment[]>();

  // Group changes by trip ID
  const changesByTrip = new Map<string, ScheduleChangeWithTrip[]>();
  for (const change of changes) {
    if (!change.tripId) {
      console.warn(
        `[convertScheduleChangesToDB] Change for ${change.tailNumber} missing trip_id, skipping`
      );
      continue;
    }

    const existing = changesByTrip.get(change.tripId) || [];
    existing.push(change);
    changesByTrip.set(change.tripId, existing);
  }

  // Convert each trip's changes to coverage segments
  for (const tripEntry of Array.from(changesByTrip.entries())) {
    const [tripId, tripChanges] = tripEntry;
    const segments: CoverageSegment[] = [];

    for (const change of tripChanges) {
      // Get new spot ID
      const newSpotId = change.newSpotId;
      if (!newSpotId) {
        console.warn(
          `[convertScheduleChangesToDB] Change for ${change.tailNumber} missing new spot ID, skipping`
        );
        continue;
      }

      // Find spot shape
      const spotShape = shapes.find(
        (s) => s.id === newSpotId && s.type === "Spot"
      );
      if (
        !spotShape ||
        !spotShape.reference_points ||
        spotShape.reference_points.length === 0
      ) {
        console.error(
          `[convertScheduleChangesToDB] Spot ${newSpotId} not found or has no reference points`
        );
        continue;
      }

      // Calculate spot center position from shape geometry
      const centroid = getCentroid(spotShape.points);

      // Y-flip coordinates to database system (bottom-left origin)
      const dbY = rampDepth - centroid.y;

      // Use center orientation (0 degrees) since we don't have placement-specific angle
      const angle = 0;

      // Convert buckets to timestamps
      const start_time = timeBucketToDate(
        change.newArrivalBucket || 0,
        startTime
      ).toISOString();
      const end_time = timeBucketToDate(
        change.newDepartureBucket || 0,
        startTime
      ).toISOString();

      segments.push({
        start_time,
        end_time,
        x: centroid.x,
        y: dbY,
        angle,
        ramp_id: rampId,
      });
    }

    if (segments.length > 0) {
      result.set(tripId, segments);
    }
  }

  return result;
}

/**
 * Extended ScheduleChange with trip ID
 * (SpotOccupancyGantt's ScheduleChange doesn't include tripId)
 */
export interface ScheduleChangeWithTrip extends ScheduleChange {
  tripId?: string;
}
